import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { ChevronRight, Circle, CircleDashed, CircleSlash, CircleX, Dot, Loader2, MinusCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { StepRecord, ResultSummary } from "../types";
import { nestSteps, summarizeSteps, describeResult, formatDuration, reasoningText } from "../utils/stepUtils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import ArtifactErrorBoundary from "./ArtifactErrorBoundary";

// A duration under this reads as noise: the backend records some steps after the work finished,
// so start and end land on the same instant and the number means nothing.
const MIN_SHOWN_MS = 100;

// Success is the default outcome, so it is a plain node on the rail rather than a green tick.
// Colour is spent only where the reader needs to look: a failure, a gap, or the live step.
const ICONS: Record<string, { Icon: LucideIcon; cls: string }> = {
    live: { Icon: Loader2, cls: "chat-v2-step-icon animate-spin text-primary" },
    interrupted: { Icon: CircleSlash, cls: "chat-v2-step-icon text-muted-foreground" },
    ok: { Icon: Circle, cls: "chat-v2-step-icon chat-v2-step-node" },
    no_data: { Icon: MinusCircle, cls: "chat-v2-step-icon text-muted-foreground" },
    partial: { Icon: CircleDashed, cls: "chat-v2-step-icon text-warning" },
    error: { Icon: CircleX, cls: "chat-v2-step-icon text-destructive" },
    unknown: { Icon: Dot, cls: "chat-v2-step-icon" },
};

const iconFor = (step: StepRecord, isLive: boolean) => {
    if (step.status === "running") return isLive ? ICONS.live : ICONS.interrupted;
    return ICONS[step.status] ?? ICONS.unknown;
};

const durationOf = (step: StepRecord): number | null =>
    step.started_ms != null && step.ended_ms != null ? step.ended_ms - step.started_ms : null;

// Brief §5.4: `label`, `detail` and `data.result_summary` are written for the reader, `error` is
// the exception that must always surface, and every other key in `data` is debug. Stated by
// exclusion, so a key the backend adds later stays hidden until we choose to show it.
const resultLine = (step: StepRecord): string =>
    step.data.result_summary != null ? describeResult(step.data.result_summary as ResultSummary) : "";

const reasoningOf = (step: StepRecord): string =>
    typeof step.data.reasoning === "string" ? step.data.reasoning : "";

// `errors` is only meaningful on a partial validate. An `ok` validate closes with `data: {}`;
// the empty array that looks like it belongs here comes from the separate `validation` frame.
const validateErrors = (step: StepRecord): string[] =>
    step.type === "validate" && step.status === "partial" && Array.isArray(step.data.errors)
        ? step.data.errors.map(String)
        : [];

const hasDetails = (step: StepRecord): boolean =>
    Boolean(step.detail) || resultLine(step) !== "" || reasoningOf(step) !== ""
    || validateErrors(step).length > 0
    || (step.status === "error" && step.data.error != null);

const StepDetails: React.FC<{ step: StepRecord }> = ({ step }) => {
    const errors = validateErrors(step);
    const reasoning = reasoningOf(step);
    const result = resultLine(step);
    return (
        <>
            {step.status === "error" && step.data.error != null && (
                <p className="chat-v2-step-note chat-v2-step-note-bad">{String(step.data.error)}</p>
            )}
            {/* `detail` on a fetch restates the subject and window that `result_summary`
                already carries, in a terser format. Show one of them, not both. */}
            {step.detail && !result && <p className="chat-v2-step-note">{step.detail}</p>}
            {result && <p className="chat-v2-step-note">{result}</p>}
            {errors.length > 0 && (
                <ul className="chat-v2-step-note chat-v2-step-errors">
                    {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
            )}
            {reasoning && (
                <div className="chat-v2-step-note chat-v2-step-reasoning">
                    <ReactMarkdown>{reasoning}</ReactMarkdown>
                </div>
            )}
        </>
    );
};

// The head is identical whether or not the step expands, so the node, the label and the time
// land on the same x on every row. A step with nothing under it gets a caret placeholder that
// holds the width but draws nothing, rather than a caret that opens an empty box.
const StepHead: React.FC<{ step: StepRecord; isLive: boolean; open: boolean; expandable: boolean }> =
    ({ step, isLive, open, expandable }) => {
        const { Icon, cls } = iconFor(step, isLive);
        const ms = durationOf(step);
        return (
            <>
                <Icon className={cls} aria-hidden />
                <ChevronRight
                    className={`chat-v2-step-caret${expandable ? "" : " is-hidden"}${open ? " is-open" : ""}`}
                    aria-hidden
                />
                <span className="chat-v2-step-name">{step.label}</span>
                {ms != null && ms >= MIN_SHOWN_MS && (
                    <span className="chat-v2-step-time">{formatDuration(ms)}</span>
                )}
            </>
        );
    };

const StepRow: React.FC<{ step: StepRecord; isLive: boolean; depth: number }> = ({ step, isLive, depth }) => {
    // A reader who opens the trail wants the thinking, so a step that reasoned starts expanded.
    // The other rows carry a line or two that the head already summarises, and they stay shut.
    // Derived rather than an initial state, so a trace that arrives while the trail is already
    // open opens its row too. `null` means nobody has clicked: one click pins the row either way.
    const [userOpen, setUserOpen] = useState<boolean | null>(null);
    const open = userOpen ?? reasoningOf(step) !== "";
    const expandable = hasDetails(step);
    const className = `chat-v2-step${depth > 0 ? " is-nested" : ""}`;

    if (!expandable) {
        return (
            <li className={className}>
                <div className="chat-v2-step-head">
                    <StepHead step={step} isLive={isLive} open={false} expandable={false} />
                </div>
            </li>
        );
    }

    return (
        <li className={className}>
            <Collapsible open={open} onOpenChange={setUserOpen}>
                <CollapsibleTrigger className="chat-v2-step-head is-clickable">
                    <StepHead step={step} isLive={isLive} open={open} expandable />
                </CollapsibleTrigger>
                <CollapsibleContent className="chat-v2-step-details">
                    <StepDetails step={step} />
                </CollapsibleContent>
            </Collapsible>
        </li>
    );
};

// Built on the shadcn Collapsible rather than AI Elements' ChainOfThought. That component takes
// `icon`, `label` and `description` as flat slots with no notion of a step that expands, so a
// per-step accordion had to be smuggled in through its `label` prop — which threw the icon off
// the label's baseline and left its own spacing and background around a closed panel. Owning
// the markup removes 23 CSS overrides that were fighting it. Spec §2.3 names this escape hatch.
const StepsAccordion: React.FC<{ steps: StepRecord[]; isLive: boolean; answerStarted?: boolean }> =
    ({ steps, isLive, answerStarted = false }) => {
    const [open, setOpen] = useState(false);

    const summary = summarizeSteps(steps);
    const liveLabel = steps.filter(s => s.status === "running").at(-1)?.label
        ?? steps.at(-1)?.label
        ?? "Thinking…";
    // The duration goes in the sentence rather than beside it, so the header reads as one
    // phrase instead of a label and a stray number.
    const doneLabel = summary.duration ? `Thought for ${summary.duration}` : "Thought";

    // The first bubble of the turn replaces the window outright: the answer takes that space,
    // it does not sit under the thinking. This also ends the thinking of an agentic turn that
    // reasons again after its first artifact — that trace stays in the trail, not in the window.
    const peekText = reasoningText(steps);
    const showPeek = isLive && !open && !answerStarted && peekText !== "";

    return (
        <div className={`chat-v2-steps${open ? " is-open" : ""}`}>
            <Collapsible open={open} onOpenChange={setOpen}>
                <CollapsibleTrigger className="chat-v2-steps-head">
                    {/* The spinner replaced the caret while live, which left the one row a reader
                        most wants to open with nothing to say it opens. They sit side by side:
                        the spinner reports the state, the caret offers the action. */}
                    {isLive && <Loader2 className="chat-v2-steps-spinner animate-spin text-primary" aria-hidden />}
                    <ChevronRight className={`chat-v2-step-caret${open ? " is-open" : ""}`} aria-hidden />
                    {/* One status region for the whole trail, not one per step: it announces the
                        step the turn has reached, and nothing else here competes with it. The
                        peek window stays aria-hidden — it repaints many times a second and would
                        drown the reader in a screen reader. */}
                    <span className="chat-v2-steps-title" role="status" aria-atomic="true">
                        {isLive ? liveLabel : doneLabel}
                    </span>
                    <span className="chat-v2-steps-meta">
                        {summary.interrupted && !isLive && <span className="chat-v2-steps-flag-warn">stopped early</span>}
                        {summary.failed > 0 && <span className="chat-v2-steps-flag-bad">{summary.failed} failed</span>}
                        {summary.partial > 0 && <span className="chat-v2-steps-flag-warn">{summary.partial} partial</span>}
                    </span>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <ol className="chat-v2-steps-list">
                        {/* One boundary per row: a record the backend shapes differently
                            tomorrow takes out its own step, not the whole trail or the answer
                            below it. A broken row renders nothing for a coach and a short
                            notice for staff. */}
                        {nestSteps(steps).map(({ step, depth }) => (
                            <ArtifactErrorBoundary key={step.id} label={`step ${step.id}`}>
                                <StepRow step={step} isLive={isLive} depth={depth} />
                            </ArtifactErrorBoundary>
                        ))}
                    </ol>
                </CollapsibleContent>
            </Collapsible>
            {/* The peek window. It holds the thinking of the turn so far, and it holds it after
                the typing stops, so a reader mid-sentence is not cut off — the answer taking
                over is what ends it. The box never grows: it is a fixed window onto the tail of
                the text, so nothing below it moves while the words arrive. It is hidden while
                the trail is open, where the same text already sits under its own step. */}
            {showPeek && (
                <div className="chat-v2-steps-peek" aria-hidden>
                    <div className="chat-v2-steps-peek-text">{peekText}</div>
                </div>
            )}
        </div>
    );
};

export default StepsAccordion;
